import React, { FC, PropsWithChildren, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Link from 'next/link';
import { Icon, IconName } from 'src/components';
import { ToastStatus } from 'src/context/notification/types';
import { useKeyboardShortcut } from 'src/hooks';
import { blurFromAllElements } from 'src/utils/browser';
import * as S from './styles';

const msgs = defineMessages({
  close: { id: 'close' },
});

const icons = {
  error: <Icon name="alertErrorOutline" />,
  warning: <Icon name="warning" />,
  success: <Icon name="tickOutline" />,
  info: <Icon name="infoOutline" />,
};

export const Toast: FC<
  PropsWithChildren<{
    status: ToastStatus;
    onClick?: () => void;
    snackBar?: boolean;
    linkText?: string;
    linkHref?: string;
    linkNewTab?: boolean;
    hideIcon?: boolean;
    hideClose?: boolean;
    customIcon?: IconName;
    pointedTip?: 'none' | 'left' | 'right';
    shouldCloseOnOutsideClick?: boolean;
    shouldCloseOnEsc?: boolean;
  }>
> = ({
  children,
  status,
  onClick,
  snackBar,
  linkText,
  linkHref,
  linkNewTab,
  hideIcon,
  hideClose,
  customIcon,
  pointedTip = 'none',
  shouldCloseOnOutsideClick = false,
  shouldCloseOnEsc = false,
}) => {
  const intl = useIntl();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClick && onClick();
    },
    [onClick],
  );

  useKeyboardShortcut('esc', () => {
    if (onClick && shouldCloseOnEsc) {
      onClick();
      blurFromAllElements();
    }
  });

  const icon = customIcon ? <Icon name={customIcon} /> : icons[status];
  return (
    <>
      <S.Container
        $status={status}
        $border={snackBar}
        $pointedTip={pointedTip}
        onClick={snackBar ? undefined : handleClick}
        data-testid="toast-container"
      >
        {!hideIcon && <S.IconWrapper>{icon}</S.IconWrapper>}
        <S.TextContainer>
          <S.Text data-testid="toast-text">{children}</S.Text>
          {linkHref && linkText && (
            <S.Footer $shiftRight={snackBar}>
              <Link href={linkHref} passHref legacyBehavior>
                <S.Link
                  target={linkNewTab ? '_blank' : undefined}
                  rel={linkNewTab ? 'noopener noreferrer' : undefined}
                >
                  {linkText}
                </S.Link>
              </Link>
            </S.Footer>
          )}
        </S.TextContainer>
        {snackBar && !hideClose && (
          <S.CloseIconWrapper onClick={handleClick} aria-label={intl.formatMessage(msgs.close)}>
            <Icon name="close" />
          </S.CloseIconWrapper>
        )}
      </S.Container>

      {shouldCloseOnOutsideClick && <S.Backdrop onClick={handleClick} />}
    </>
  );
};

export default Toast;
